/**
 * Job Types Hooks
 * React Query hooks for fetching and managing job type data
 *
 * Following DDD Principles:
 * - Hooks consume job-types service
 * - React Query handles caching and state management
 * - Components receive DTOs ready for display
 * - NO business logic in hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { jobTypesService } from '@/services/job-types.service';
import type {
  JobTypeDto,
  CreateJobTypeRequest,
  UpdateJobTypeRequest,
} from '@/types/job-type';

/**
 * Job Type query key factory
 * Provides consistent cache keys for React Query
 */
export const jobTypeKeys = {
  all: ['job-types'] as const,
  lists: () => [...jobTypeKeys.all, 'list'] as const,
  list: (activeOnly?: boolean) => [...jobTypeKeys.lists(), { activeOnly }] as const,
  details: () => [...jobTypeKeys.all, 'detail'] as const,
  detail: (id: string) => [...jobTypeKeys.details(), id] as const,
};

/**
 * Hook to fetch list of job types
 * @param activeOnly - If true, only fetch active job types
 */
export function useJobTypes(activeOnly?: boolean) {
  return useQuery<JobTypeDto[], Error>({
    queryKey: jobTypeKeys.list(activeOnly),
    queryFn: () => jobTypesService.listJobTypes(activeOnly),
    staleTime: 5 * 60 * 1000, // 5 minutes - job types don't change often
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false, // Job types rarely change
    retry: 2,
  });
}

/**
 * Hook to fetch single job type by ID
 */
export function useJobType(id?: string) {
  return useQuery<JobTypeDto, Error>({
    queryKey: jobTypeKeys.detail(id ?? ''),
    queryFn: async () => {
      if (!id) {
        throw new Error('Job type ID is required');
      }
      return jobTypesService.getJobType(id);
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
  });
}

/**
 * Hook to create a new job type
 *
 * Implements optimistic updates for better UX.
 */
export function useCreateJobType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateJobTypeRequest) => jobTypesService.createJobType(data),

    // Optimistic update: Add to cache immediately
    onMutate: async (newJobType) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: jobTypeKeys.lists() });

      // Snapshot previous state
      const previousJobTypes = queryClient.getQueryData(jobTypeKeys.lists());

      // Optimistically update cache with temporary ID
      queryClient.setQueryData<JobTypeDto[]>(jobTypeKeys.lists(), (old = []) => [
        ...old,
        {
          id: `temp-${Date.now()}`,
          providerId: 'temp',
          name: newJobType.name,
          description: newJobType.description,
          isActive: true,
          fieldDefinitions: newJobType.fieldDefinitions || [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        } as JobTypeDto,
      ]);

      return { previousJobTypes };
    },

    // On error: Rollback
    onError: (_err, _newJobType, context) => {
      if (context?.previousJobTypes) {
        queryClient.setQueryData(jobTypeKeys.lists(), context.previousJobTypes);
      }
    },

    // Always refetch to ensure consistency
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: jobTypeKeys.all });
    },
  });
}

/**
 * Hook to update an existing job type
 *
 * Implements optimistic updates for better UX.
 */
export function useUpdateJobType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateJobTypeRequest }) =>
      jobTypesService.updateJobType(id, data),

    // Optimistic update: Update in cache immediately
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: jobTypeKeys.lists() });
      await queryClient.cancelQueries({ queryKey: jobTypeKeys.detail(id) });

      // Snapshot previous state
      const previousJobTypes = queryClient.getQueryData(jobTypeKeys.lists());
      const previousJobType = queryClient.getQueryData(jobTypeKeys.detail(id));

      // Optimistically update lists cache
      queryClient.setQueryData<JobTypeDto[]>(jobTypeKeys.lists(), (old = []) =>
        old.map((jt) =>
          jt.id === id
            ? {
                ...jt,
                ...data,
                updatedAt: new Date().toISOString(),
              }
            : jt
        )
      );

      // Optimistically update detail cache
      queryClient.setQueryData<JobTypeDto>(jobTypeKeys.detail(id), (old) => {
        if (!old) return old;
        return {
          ...old,
          ...data,
          updatedAt: new Date().toISOString(),
        };
      });

      return { previousJobTypes, previousJobType };
    },

    // On error: Rollback
    onError: (_err, { id }, context) => {
      if (context?.previousJobTypes) {
        queryClient.setQueryData(jobTypeKeys.lists(), context.previousJobTypes);
      }
      if (context?.previousJobType) {
        queryClient.setQueryData(jobTypeKeys.detail(id), context.previousJobType);
      }
    },

    // Always refetch to ensure consistency
    onSettled: (_data, _error, { id }) => {
      queryClient.invalidateQueries({ queryKey: jobTypeKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: jobTypeKeys.lists() });
    },
  });
}

/**
 * Hook to delete a job type
 *
 * Implements optimistic updates for better UX.
 */
export function useDeleteJobType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => jobTypesService.deleteJobType(id),

    // Optimistic update: Remove from cache immediately
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: jobTypeKeys.lists() });

      // Snapshot previous state
      const previousJobTypes = queryClient.getQueryData(jobTypeKeys.lists());

      // Optimistically remove from cache
      queryClient.setQueryData<JobTypeDto[]>(jobTypeKeys.lists(), (old = []) =>
        old.filter((jt) => jt.id !== id)
      );

      return { previousJobTypes };
    },

    // On error: Rollback
    onError: (_err, _id, context) => {
      if (context?.previousJobTypes) {
        queryClient.setQueryData(jobTypeKeys.lists(), context.previousJobTypes);
      }
    },

    // Always refetch to ensure consistency
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: jobTypeKeys.all });
    },
  });
}

/**
 * Convenience hook for fetching only active job types
 * (most common use case for job creation)
 */
export function useActiveJobTypes() {
  return useJobTypes(true);
}
